import { Clock3 } from "lucide-react";
import { useMemo, useState } from "react";
import { useNavigate } from "react-router";
import ExamBreakdown from "@/components/ExamBreakdown";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import type { ResultsDashboardModel, ResultsTopic } from "@/types/results";
import { RecentAttemptCard, ResultsTopicCard } from "./ResultsPrimitives";
import { ResultsTopicDialog } from "./ResultsTopicDialog";
import { useTranslation } from "react-i18next";

const formatAnswerTime = (seconds: number | null) => {
  if (seconds == null) return "—";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const rounded = Math.round(seconds);
  return `${Math.floor(rounded / 60)}:${String(rounded % 60).padStart(2, "0")}`;
};

function OverallResultsCard({ model }: { model: ResultsDashboardModel }) {
  const { t } = useTranslation();
  const ready = model.overallScore >= model.passingScore;
  const scoreSummary = model.overallScoreChange == null
    ? t("results.first_period")
    : `${model.overallScore - model.overallScoreChange}% → ${model.overallScore}%${model.comparisonDateLabel ? ` ${t("results.since_date", { date: model.comparisonDateLabel })}` : ""}`;
  const timeChange = model.averageTimeChangeSeconds;

  return (
    <section className="flex min-h-[226px] min-w-0 flex-col gap-4 overflow-hidden rounded-[28px] bg-[var(--dp-brand-deep)] p-6 text-white xl:col-span-2">
      <div className="flex items-center justify-between gap-4">
        <span className="inline-flex h-8 items-center rounded-full bg-white/[0.08] px-3 text-sm font-semibold leading-[21px] tracking-[0.07px] text-[#e0b341]">
          {ready ? t("results.ready") : t("results.not_ready")}
        </span>
        <span className="text-sm font-medium leading-[21px] tracking-[0.07px] text-[#c3c6cc]">{t("results.momentum")}</span>
      </div>
      <div className="flex items-center gap-3">
        <strong className="text-[48px] font-bold leading-[52px] tracking-[-0.18px] text-[var(--dp-brand-button)]">
          {model.overallScore}<span className="text-lg leading-6">%</span>
        </strong>
        <div>
          <h1 className="text-lg font-semibold leading-[27px]">{t("results.overall_score")}</h1>
          <p className="text-sm leading-[21px] tracking-[0.07px] text-[#c3c6cc]">{scoreSummary}</p>
        </div>
      </div>
      <div className="relative mt-auto h-3 overflow-visible rounded-full bg-white/[0.14]">
        <div className="h-full rounded-full bg-[var(--dp-brand-button)]" style={{ width: `${Math.min(model.overallScore, 100)}%` }} />
        <span
          aria-hidden="true"
          className="absolute -top-1 h-5 w-1 bg-[#e0b341]"
          style={{ left: `calc(${model.passingScore}% - 2px)` }}
        />
      </div>
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2 text-sm font-semibold leading-[21px] tracking-[0.07px] text-[#c3c6cc]">
          <Clock3 aria-hidden="true" className="h-4 w-4" />
          {t("results.per_answer", { time: formatAnswerTime(model.averageAnswerTimeSeconds) })}
          {timeChange != null && (
            <span className="inline-flex h-8 items-center rounded-full bg-white/[0.08] px-3 text-[#e0b341]">
              {timeChange <= 0
                ? t("results.faster", { seconds: Math.abs(timeChange) })
                : t("results.slower", { seconds: Math.abs(timeChange) })}
            </span>
          )}
        </div>
        <span className="text-sm font-semibold leading-[21px] tracking-[0.07px] text-[#e0b341]">{t("results.to_pass", { score: model.passingScore })}</span>
      </div>
    </section>
  );
}

export function ResultsDashboard({ model }: { model: ResultsDashboardModel }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [selectedTopic, setSelectedTopic] = useState<ResultsTopic | null>(null);
  const [passingCriteriaOpen, setPassingCriteriaOpen] = useState(false);
  const orderedTopics = useMemo(
    () => [...model.topics].sort((a, b) => a.sectionId - b.sectionId),
    [model.topics],
  );

  return (
    <>
      <div className="dp-page-container flex flex-col gap-6 p-6">
        <div className="grid w-full grid-cols-1 gap-6 xl:grid-cols-3">
          <OverallResultsCard model={model} />
          <RecentAttemptCard attempt={model.recentAttempt} onViewAll={() => navigate("/past-exams")} />
        </div>
        <section className="flex flex-col gap-4 py-6">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold leading-[27px] text-[#0a0c11]">{t("results.performance_breakdown")}</h2>
            <span className="h-px min-w-0 flex-1" />
            <button
              className="dp-button-secondary h-9 px-4 text-sm leading-5 outline-none focus-visible:ring-2 focus-visible:ring-[var(--dp-brand-button)] focus-visible:ring-offset-2"
              onClick={() => setPassingCriteriaOpen(true)}
              type="button"
            >
              {t("results.see_passing_criteria").replace(" →", "")}
            </button>
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
            {orderedTopics.map((topic) => (
              <ResultsTopicCard
                key={topic.sectionId}
                onClick={() => setSelectedTopic(topic)}
                passingScore={model.passingScore}
                topic={topic}
              />
            ))}
          </div>
        </section>
      </div>
      <ResultsTopicDialog
        comparisonDateLabel={model.comparisonDateLabel}
        onOpenChange={(open) => !open && setSelectedTopic(null)}
        open={selectedTopic != null}
        topic={selectedTopic}
      />
      <Dialog onOpenChange={setPassingCriteriaOpen} open={passingCriteriaOpen}>
        <DialogContent className="flex h-[min(720px,calc(100dvh-48px))] max-w-[920px] flex-col gap-0 overflow-hidden rounded-[28px] p-0">
          <DialogHeader className="shrink-0 border-b border-[#dddfe4] px-6 py-5 text-left">
            <DialogTitle>{t("results.passing_criteria_title", { defaultValue: "Passing criteria" })}</DialogTitle>
            <DialogDescription className="sr-only">{t("results.passing_criteria_description")}</DialogDescription>
          </DialogHeader>
          <div className="min-h-0 flex-1 overflow-hidden p-6">
            <ExamBreakdown />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
